import React, {FC, useMemo, useState} from 'react';
import {
    Box,
    Card,
    CardContent,
    FormControl,
    FormControlLabel,
    Grid,
    Radio,
    RadioGroup,
    Stack,
    Typography,
    useTheme
} from '@mui/material';
import {BarChart} from '@mui/x-charts/BarChart';
import {
    TimelinePeriod,
    TimelineReportRequest,
} from "../../../types/models/LogGroups";
import {useGetLogTimelineEnteredQuery, useGetLogTimelineShippedQuery} from "../../../apis/Log.api.ts";
import {useGetCordwoodTimelineShippedQuery} from "../../../apis/Cordwood.api.ts";
import {array, date, object, string, TypeOf} from "zod";
import {FormProvider, useForm, useWatch} from "react-hook-form";
import {zodResolver} from "@hookform/resolvers/zod";
import {
    useGetCuttingPresentationQuery,
} from "../../../apis/Cutting.api.ts";
import {useGetTimberTypePresentationQuery} from "../../../apis/TimberType.api.ts";
import {useGetTimberClassPresentationQuery} from "../../../apis/TimberClass.api.ts";
import {AsyncFormSelect, FormDateRange, FormSelect} from "../../../../share";
import {toString as toStringCutting} from "../../../types/models/Cutting.ts";
import {toString as toStringTimberStorage} from "../../../types/models/TimberStorage.ts";
import {toString as toStringTimberType} from "../../../types/models/TimberType.ts";
import {toString as toStringTimberClass} from "../../../types/models/TimberClass.ts";
import {formatDateTimeParam} from "../../../../utils/formatDate.ts";
import {useGetTimberStoragePresentationQuery} from "../../../apis/TimberStorage.api.ts";
import * as crudgen from "../../../../share";
import {ShipmentTypePairs, toString as toStringShipmentType} from "../../../types/models/ShipmentType.d";
import {emptyListPaginationResult, listPaginationLimits} from "../../../../types/global.ts";
import useSearchDebounce from "../../../../hooks/useSearchDebounce.ts";
import {t} from "@lingui/core/macro";
import {CuttingNameDisplay} from "../../cutting/CuttingDetail/CuttingName.tsx";
import CuttingName from "../../cutting/CuttingDetail/CuttingName.tsx";
import {TimberStorageNameDisplay} from "../../timberStorage/TimberStorageDetail/TimberStorageName.tsx";
import TimberStorageName from "../../timberStorage/TimberStorageDetail/TimberStorageName.tsx";
import {TimberTypeNameDisplay} from "../../timberType/TimberTypeDetail/TimberTypeName.tsx";
import TimberTypeName from "../../timberType/TimberTypeDetail/TimberTypeName.tsx";
import {TimberClassNameDisplay} from "../../timberClass/TimberClassDetail/TimberClassName.tsx";
import TimberClassName from "../../timberClass/TimberClassDetail/TimberClassName.tsx";

const initialFormState = {
    shipmentType: '',
    typeId: [],
    classId: [],
    cuttingId: [],
    storageId: [],
    startCreate: null,
    endCreate: null,
    startShipment: null,
    endShipment: null,
}

const formSchema = object({
    shipmentType: string().nullish(),
    typeId: array(string()).nullish(),
    classId: array(string()).nullish(),
    cuttingId: array(string()).nullish(),
    storageId: array(string()).nullish(),
    startCreate: date().nullish(),
    endCreate: date().nullish(),
    startShipment: date().nullish(),
    endShipment: date().nullish(),
});

type FormInput = TypeOf<typeof formSchema>;

interface TimelineReportProps {
    filter?: Record<string, string[] | undefined>;
    showCutting?: boolean;
    showStorage?: boolean;
    showForm?: boolean;
    initialState: FormInput;
    initialPeriod?: TimelinePeriod;
}

const TimelineRootReport: FC<TimelineReportProps> = ({
                                                         filter: externalFilter,
                                                         showCutting = true,
                                                         showStorage = true,
                                                         showForm = true,
                                                         initialState = initialFormState,
                                                         initialPeriod = 'Day',
}) => {
    const theme = useTheme();
    const [period, setPeriod] = useState<TimelinePeriod>(initialPeriod);

    const initialFormInputState = {
        ...initialFormState,
        ...initialState,
    }
    const methods = useForm<FormInput>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            ...initialFormInputState,
            ...externalFilter,
            cuttingId: Array.isArray(externalFilter?.cuttingId) ? externalFilter.cuttingId : initialFormInputState.cuttingId,
            storageId: Array.isArray(externalFilter?.storageId) ? externalFilter.storageId : initialFormInputState.storageId,
        }
    });

    const [classId, typeId, cuttingId, storageId, shipmentType, startCreate, endCreate, startShipment, endShipment] = useWatch({
        control: methods.control,
        name: ['classId', 'typeId', 'cuttingId', 'storageId', 'shipmentType', 'startCreate', 'endCreate', 'startShipment', 'endShipment']
    })

    const filter = useMemo(() => ({
        classId,
        typeId,
        shipmentType,
        cuttingId: (cuttingId && cuttingId.length > 0) ? cuttingId : externalFilter?.cuttingId,
        providerId: externalFilter?.providerId,
        storageId: (storageId && storageId.length > 0) ? storageId : externalFilter?.storageId,
        startCreate: formatDateTimeParam(startCreate),
        endCreate: formatDateTimeParam(endCreate),
        startShipment: formatDateTimeParam(startShipment),
        endShipment: formatDateTimeParam(endShipment),
        shipmentDefined: externalFilter?.shipmentDefined,
    }), [classId, typeId, cuttingId, storageId, startCreate, endCreate, startShipment, endShipment, externalFilter]);

    const [, setCuttingInputValue, cuttingInputTerm] = useSearchDebounce();
    const {
        data: cuttingList = emptyListPaginationResult,
        isLoading: getCuttingLoading,
        isFetching: getCuttingFetching,
    } = useGetCuttingPresentationQuery({
        list: listPaginationLimits,
        filter: {
            name: cuttingInputTerm,
        },
    }, {skip: !showCutting})

    const [, setStorageInputValue, storageInputTerm] = useSearchDebounce();
    const {
        data: storageList = emptyListPaginationResult,
        isLoading: getStorageLoading,
        isFetching: getStorageFetching,
    } = useGetTimberStoragePresentationQuery({
        list: listPaginationLimits,
        filter: {
            name: storageInputTerm,
            cuttingIdRefs: filter.cuttingId,
        },
    }, {skip: !showStorage})

    const [, setTimberTypeInputValue, timberTypeInputTerm] = useSearchDebounce();
    const {
        data: timberTypeList = emptyListPaginationResult,
        isLoading: getTimberTypeListLoading,
        isFetching: getTimberTypeListFetching,
    } = useGetTimberTypePresentationQuery({
        list: listPaginationLimits,
        filter: {
            name: timberTypeInputTerm,
        },
    })

    const [, setTimberClassInputValue, timberClassInputTerm] = useSearchDebounce();
    const {
        data: timberClassList = emptyListPaginationResult,
        isLoading: getTimberClassListLoading,
        isFetching: getTimberClassListFetching,
    } = useGetTimberClassPresentationQuery({
        list: listPaginationLimits,
        filter: {
            name: timberClassInputTerm,
        },
    })

    const timelineRequest: TimelineReportRequest = useMemo(() => ({
        filter,
        period,
    }), [filter, period]);

    const {data: logEnteredDataRaw = [], isFetching: isLogEnteredFetching} = useGetLogTimelineEnteredQuery(timelineRequest, {skip: shipmentType == 'Cordwood'});
    const {data: logShippedDataRaw = [], isFetching: isLogShippedFetching} = useGetLogTimelineShippedQuery(timelineRequest, {skip: shipmentType == 'Cordwood'});
    const {data: cordwoodShippedDataRaw = [], isFetching: isCordwoodShippedFetching} = useGetCordwoodTimelineShippedQuery(timelineRequest, {skip: shipmentType == 'Logs'});

    const logEnteredData = useMemo(() =>
            Array.isArray(logEnteredDataRaw) && shipmentType != 'Cordwood' ? logEnteredDataRaw : (logEnteredDataRaw as any)?.data || [],
        [logEnteredDataRaw, shipmentType]
    );
    const logShippedData = useMemo(() =>
            Array.isArray(logShippedDataRaw) && shipmentType != 'Cordwood' ? logShippedDataRaw : (logShippedDataRaw as any)?.data || [],
        [logShippedDataRaw, shipmentType]
    );
    const cordwoodShippedData = useMemo(() =>
            Array.isArray(cordwoodShippedDataRaw) && shipmentType != 'Logs' ? cordwoodShippedDataRaw : (cordwoodShippedDataRaw as any)?.data || [],
        [cordwoodShippedDataRaw, shipmentType]
    );

    const allDates = useMemo(() => {
        const dates = new Set<string>();
        if (Array.isArray(logEnteredData)) logEnteredData.forEach(d => dates.add(d.date));
        if (Array.isArray(logShippedData)) logShippedData.forEach(d => dates.add(d.date));
        if (Array.isArray(cordwoodShippedData)) cordwoodShippedData.forEach(d => dates.add(d.date));
        const sortedDates = Array.from(dates).sort();
        return sortedDates;
    }, [logEnteredData, logShippedData, cordwoodShippedData]);

    const chartData = useMemo(() => {
        const logEnteredMap = new Map(Array.isArray(logEnteredData) ? logEnteredData.map(d => [d.date, { count: d.totalCount, volume: d.totalVolume }]) : []);
        const logShippedMap = new Map(Array.isArray(logShippedData) ? logShippedData.map(d => [d.date, { shipmentCount: d.shipmentCount, count: d.totalCount, volume: d.totalVolume }]) : []);
        const cordwoodShippedMap = new Map(Array.isArray(cordwoodShippedData) ? cordwoodShippedData.map(d => [d.date, { shipmentCount: d.shipmentCount, count: d.totalCount, volume: d.totalVolume }]) : []);

        const data = allDates.map(date => ({
            date,
            logEntered: logEnteredMap.get(date)?.count || 0,
            logEnteredVolume: logEnteredMap.get(date)?.volume || 0,
            logShipped: logShippedMap.get(date)?.count || 0,
            logShippedVolume: logShippedMap.get(date)?.volume || 0,
            cordwoodShipped: cordwoodShippedMap.get(date)?.count || 0,
            cordwoodShippedVolume: cordwoodShippedMap.get(date)?.volume || 0,
            logShipments: logShippedMap.get(date)?.shipmentCount || 0,
            cordwoodShipments: cordwoodShippedMap.get(date)?.shipmentCount || 0,
        }));
        return data;
    }, [allDates, logEnteredData, logShippedData, cordwoodShippedData]);

    const volumeGraph = useMemo(() => {
        const data = []
        if (shipmentType != 'Cordwood') {
            data.push(
                {
                    dataKey: 'logEnteredVolume',
                    label: 'Logs Entered Volume',
                    color: '#ed0202',
                    valueFormatter: (v) => v === null ? '' : v.toFixed(2),
                }
            )
            data.push(
                {
                    dataKey: 'logShippedVolume',
                    label: 'Logs Shipped Volume',
                    stack: 'shipped',
                    color: '#ed6c02',
                    valueFormatter: (v) => v === null ? '' : v.toFixed(2),
                }
            )
        }
        if (shipmentType != 'Logs') {
            data.push(
                {
                    dataKey: 'cordwoodShippedVolume',
                    label: 'Cordwood Shipped Volume',
                    stack: '#2e7d32',
                    color: theme.palette.success.main,
                    valueFormatter: (v) => v === null ? '' : v.toFixed(2),
                },
            )
        }
        return data
    }, [ shipmentType ])

    const shipmentsGraph = useMemo(() => {
        const data = []
        if (shipmentType != 'Cordwood') {
            data.push(
                {
                    dataKey: 'logShipments',
                    label: 'Logs Shipments',
                    stack: 'shipped',
                    color: '#ed6c02',
                    valueFormatter: (v) => v === null ? '' : v.toString(),
                },
            )
        }
        if (shipmentType != 'Logs') {
            data.push(
                {
                    dataKey: 'cordwoodShipments',
                    label: 'Cordwood Shipments',
                    stack: '#2e7d32',
                    color: theme.palette.success.main,
                    valueFormatter: (v) => v === null ? '' : v.toString(),
                },
            )
        }
        return data
    }, [ shipmentType ])

    return (
        <Box sx={{mt: 4}}>
            <Card>
                <CardContent>
                    <Grid container spacing={2} sx={{ alignItems: "center", mb: 2}}>
                        <Grid item xs={12} sm={4}>
                            <Typography variant="h6">{t`Timeline Report`}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={8} sx={{display: 'flex', justifyContent: 'flex-end', gap: 2}}>
                            <FormControl component="fieldset">
                                <RadioGroup
                                    row
                                    name="period"
                                    value={period}
                                    onChange={(e) => setPeriod(e.target.value as TimelinePeriod)}
                                >
                                    <FormControlLabel value="Day" control={<Radio/>} label={t`Day`}/>
                                    <FormControlLabel value="Month" control={<Radio/>} label={t`Month`}/>
                                </RadioGroup>
                            </FormControl>
                        </Grid>
                    </Grid>
                    { showForm &&
                        <Box sx={{ mb: 2 }}>
                        <FormProvider {...methods}>
                            <FormSelect
                                name='shipmentType'
                                label={t`Shipment Type`}
                                options={ShipmentTypePairs}
                                optionLabelMap={toStringShipmentType}
                                optionsLoading={false}
                            />
                            {showCutting && (
                                <AsyncFormSelect
                                    name='cuttingId'
                                    label={t`Cutting`}
                                    options={cuttingList.items}
                                    optionsLoading={getCuttingLoading || getCuttingFetching}
                                    optionLabelMap={toStringCutting}
                                    onInputChange={setCuttingInputValue}
                                    multiple
                                    renderOption={(optionProps, option) => {
                                        const {key, ...filteredOptionProps} = optionProps;
                                        return (<li key={key} {...filteredOptionProps}><CuttingNameDisplay data={option}/></li>)
                                    }}
                                    renderValue={(value, getItemProps) =>
                                        value.map((option: any, index: number) => {
                                            const {key, ...tagProps} = getItemProps({index})
                                            return (
                                                <crudgen.SelectChip key={key} {...tagProps}>
                                                    <CuttingName id={option}/>
                                                </crudgen.SelectChip>
                                            )
                                        })
                                    }
                                />
                            )}
                            {showStorage && (
                                <AsyncFormSelect
                                    name='storageId'
                                    label={t`Storage`}
                                    options={storageList.items}
                                    optionsLoading={getStorageLoading || getStorageFetching}
                                    optionLabelMap={toStringTimberStorage}
                                    onInputChange={setStorageInputValue}
                                    multiple
                                    renderOption={(optionProps, option) => {
                                        const {key, ...filteredOptionProps} = optionProps;
                                        return (<li key={key} {...filteredOptionProps}><TimberStorageNameDisplay data={option}/></li>)
                                    }}
                                    renderValue={(value, getItemProps) =>
                                        value.map((option: any, index: number) => {
                                            const {key, ...tagProps} = getItemProps({index})
                                            return (
                                                <crudgen.SelectChip key={key} {...tagProps}>
                                                    <TimberStorageName id={option}/>
                                                </crudgen.SelectChip>
                                            )
                                        })
                                    }
                                />
                            )}
                            <Stack direction="row" spacing={1}>
                                <AsyncFormSelect
                                    name='typeId'
                                    label={t`Timber Type`}
                                    options={timberTypeList.items}
                                    optionsLoading={getTimberTypeListLoading || getTimberTypeListFetching}
                                    optionLabelMap={toStringTimberType}
                                    onInputChange={setTimberTypeInputValue}
                                    multiple
                                    renderOption={(optionProps, option) => {
                                        const {key, ...filteredOptionProps} = optionProps;
                                        return (<li key={key} {...filteredOptionProps}><TimberTypeNameDisplay data={option}/></li>)
                                    }}
                                    renderValue={(value, getItemProps) =>
                                        value.map((option: any, index: number) => {
                                            const {key, ...tagProps} = getItemProps({index})
                                            return (
                                                <crudgen.SelectChip key={key} {...tagProps}>
                                                    <TimberTypeName id={option}/>
                                                </crudgen.SelectChip>
                                            )
                                        })
                                    }
                                />
                                <AsyncFormSelect
                                    name='classId'
                                    label={t`Timber Class`}
                                    options={timberClassList.items}
                                    optionsLoading={getTimberClassListLoading || getTimberClassListFetching}
                                    optionLabelMap={toStringTimberClass}
                                    onInputChange={setTimberClassInputValue}
                                    multiple
                                    renderOption={(optionProps, option) => {
                                        const {key, ...filteredOptionProps} = optionProps;
                                        return (<li key={key} {...filteredOptionProps}><TimberClassNameDisplay data={option}/></li>)
                                    }}
                                    renderValue={(value, getItemProps) =>
                                        value.map((option: any, index: number) => {
                                            const {key, ...tagProps} = getItemProps({index})
                                            return (
                                                <crudgen.SelectChip key={key} {...tagProps}>
                                                    <TimberClassName id={option}/>
                                                </crudgen.SelectChip>
                                            )
                                        })
                                    }
                                />
                            </Stack>
                            <FormDateRange
                                startName="startCreate"
                                endName="endCreate"
                                startLabel={t`From`}
                                endLabel={t`To Timber Input Date`}
                            />
                            <FormDateRange
                                startName="startShipment"
                                endName="endShipment"
                                startLabel={t`From`}
                                endLabel={t`To Shipment Date`}
                            />
                        </FormProvider>
                    </Box>
                    }

                    <Box sx={{width: '100%', height: 400, mb: 4}}>
                        <Typography variant="subtitle1" sx={{ ml: 2 }}>Volume</Typography>
                        <BarChart
                            dataset={chartData}
                            xAxis={[{
                                dataKey: 'date',
                                scaleType: 'band',
                                categoryGapRatio: 0.1,
                                label: t`Date`,
                                tickLabelStyle: {
                                    textAnchor: 'middle',
                                    fontSize: 10,
                                },
                            }]}
                            series={volumeGraph}
                            height={400}
                            margin={{left: 50, right: 50, top: 50, bottom: 100}}
                            slotProps={{
                                legend: {
                                    direction: 'row',
                                    position: {vertical: 'top', horizontal: 'middle'},
                                    padding: 0,
                                },
                            }}
                        />
                    </Box>

                    { shipmentType != 'Cordwood' &&
                        <Box sx={{width: '100%', height: 400, mb: 4}}>
                            <Typography variant="subtitle1" sx={{ ml: 2 }}>{t`Logs Count`}</Typography>
                            <BarChart
                                dataset={chartData}
                                xAxis={[{
                                    dataKey: 'date',
                                    scaleType: 'band',
                                    categoryGapRatio: 0.1,
                                    label: t`Date`,
                                    tickLabelStyle: {
                                        textAnchor: 'middle',
                                        fontSize: 10,
                                    },
                                }]}
                                series={[
                                    {
                                        dataKey: 'logEntered',
                                        label: 'Logs Entered',
                                        color: '#ed0202',
                                        valueFormatter: (v) => v === null ? '' : v.toString(),
                                    },
                                    {
                                        dataKey: 'logShipped',
                                        label: 'Logs Shipped',
                                        color: '#ed6c02',
                                        valueFormatter: (v) => v === null ? '' : v.toString(),
                                    },
                                ]}
                                height={400}
                                margin={{left: 50, right: 50, top: 50, bottom: 100}}
                                slotProps={{
                                    legend: {
                                        direction: 'row',
                                        position: {vertical: 'top', horizontal: 'middle'},
                                        padding: 0,
                                    },
                                }}
                            />
                        </Box>
                    }

                    <Box sx={{width: '100%', height: 400, mb: 4}}>
                        <Typography variant="subtitle1" sx={{ ml: 2 }}>Shipments</Typography>
                        <BarChart
                            dataset={chartData}
                            xAxis={[{
                                dataKey: 'date',
                                scaleType: 'band',
                                categoryGapRatio: 0.1,
                                label: t`Date`,
                                tickLabelStyle: {
                                    textAnchor: 'middle',
                                    fontSize: 10,
                                },
                            }]}
                            series={shipmentsGraph}
                            height={400}
                            margin={{left: 50, right: 50, top: 50, bottom: 100}}
                            slotProps={{
                                legend: {
                                    direction: 'row',
                                    position: {vertical: 'top', horizontal: 'middle'},
                                    padding: 0,
                                },
                            }}
                        />
                    </Box>
                </CardContent>
            </Card>
        </Box>
    );
};

export default TimelineRootReport;
